import {SECONDS_IN_HOUR} from '@wandb/weave/common/util/time';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC, Fragment} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from 'semantic-ui-react';

import {createDismissibleBanner} from '../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {Analytics} from '../../../services/analytics';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {GlobalBannerState, useGetFilteredGlobalBannerStates} from '..';
import {
  BillingAdminMonthlyCTAText,
  NonBillingAdminMonthlyCTAText,
} from './BannerText';

export type ExceededTeamsPlanLimitBannerProps = {
  organizationId: string;
  billingInterval: 'monthly' | 'annual';
  savingsIfSwitchToAnnual?: number;
  organizationName: string;
  computeSecondsUsed: number;
  computeSecondsLimit: number;
  isBillingAdmin: boolean;
  billingAdminEmail?: string | null;
};

export const ExceededTeamsPlanLimitBanner: FC = () => {
  const states = useGetFilteredGlobalBannerStates();

  if (states == null) {
    return null;
  }

  return (
    <Tailwind>
      <div className="mb-8 space-y-2">
        <ExceededTeamsPlanLimitBannerStack states={states} />
      </div>
    </Tailwind>
  );
};

const ExceededTeamsPlanLimitBannerStack: React.FC<{
  states: GlobalBannerState[];
}> = ({states}) => {
  return (
    <>
      {states.map(state => {
        return (
          <Fragment key={state.account.name + state.account.accountType}>
            {state.teamsPlanEnforcementBannerFlags
              .exceededTeamsPlanLimitBannerProps && (
              <ExceededTeamsPlanLimitBannerComp
                {...state.teamsPlanEnforcementBannerFlags
                  .exceededTeamsPlanLimitBannerProps}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const ExceededTeamsPlanLimitBannerComp =
  createDismissibleBanner<ExceededTeamsPlanLimitBannerProps>({
    id: ({organizationId}) => {
      return organizationId + ':teams-plan-exceeded';
    },
    Component: ({
      onDismiss,
      organizationId,
      billingInterval,
      organizationName,
      computeSecondsLimit,
      computeSecondsUsed,
      isBillingAdmin,
      billingAdminEmail,
      savingsIfSwitchToAnnual,
    }) => {
      const computeHoursUsedText = (
        computeSecondsUsed / SECONDS_IN_HOUR
      ).toLocaleString('en-us', {maximumFractionDigits: 0});
      const computeHoursLimitText = (
        computeSecondsLimit / SECONDS_IN_HOUR
      ).toLocaleString('en-us', {maximumFractionDigits: 0});
      return (
        <Banner
          variant={'warning'}
          eventData={{
            location: `exceeded limit for teams plan alert banner`,
            organizationId,
          }}>
          <BannerFlexWrapper>
            <div className="text-xs">
              <Icon name="info" />
            </div>
            <BannerTextWrapper>
              <span>
                <BannerBoldText>
                  You've used {computeHoursUsedText} of your{' '}
                  {computeHoursLimitText} {billingInterval} tracking hours.
                </BannerBoldText>{' '}
                Each additional hour is being charged at $1 per hour.{' '}
                {billingInterval === 'monthly' && (
                  <>
                    {isBillingAdmin || billingAdminEmail == null ? (
                      <BillingAdminMonthlyCTAText
                        organizationName={organizationName}
                        savingsIfSwitchToAnnual={savingsIfSwitchToAnnual}
                      />
                    ) : (
                      <NonBillingAdminMonthlyCTAText
                        billingAdminEmail={billingAdminEmail}
                        savingsIfSwitchToAnnual={savingsIfSwitchToAnnual}
                      />
                    )}{' '}
                  </>
                )}
              </span>
            </BannerTextWrapper>
          </BannerFlexWrapper>
          <button
            className="flex items-center border-none bg-transparent font-semibold text-sienna-600 hover:cursor-pointer hover:text-sienna-500"
            onClick={() => {
              Analytics.track('Dismiss button clicked', {
                location: `exceeded 85% for teams plan limit alert banner`,
                organizationId,
              });
              onDismiss();
            }}>
            Dismiss
          </button>
        </Banner>
      );
    },
  });
